import type {
  WizardComponentAdapterFn,
  WizardComponentAdapterFunctionalComponent,
} from '@lemonade-hq/chat-wizard-client-base';
import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseWizardAsyncComponents<TComponentType extends string = string> {
  readonly isLoading: boolean;
  readonly components: WizardComponentAdapterFunctionalComponent<TComponentType>[];
  readonly error?: Error;
}

export const useWizardAsyncComponents = <TComponentType extends string = string>(
  getComponentsFns: WizardComponentAdapterFn<TComponentType>[] = [],
): UseWizardAsyncComponents<TComponentType> => {
  const getComponentsRef = useRef<WizardComponentAdapterFn<TComponentType>[]>(getComponentsFns);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error>();
  const [components, setComponents] = useState<WizardComponentAdapterFunctionalComponent<TComponentType>[]>([]);

  const loadComponents = useCallback(async (): Promise<WizardComponentAdapterFunctionalComponent<TComponentType>[]> => {
    // eslint-disable-next-line @typescript-eslint/promise-function-async
    return await Promise.all(getComponentsFns.map(getComponent => getComponent()));
  }, [getComponentsFns]);

  useEffect(() => {
    setIsLoading(true);

    if (getComponentsRef.current !== getComponentsFns) {
      setComponents([]);
    }

    getComponentsRef.current = getComponentsFns;

    void loadComponents()
      .then(resolvedComponents => {
        setComponents(resolvedComponents);
      })
      .catch(err => {
        setError(err as Error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [getComponentsFns, loadComponents]);

  return {
    isLoading,
    components: getComponentsRef.current !== getComponentsFns ? [] : components,
    error,
  };
};
